import React from 'react';
import { OnboardingToolbar } from './OnboardingToolbar';
import { ONBOARDING_STEP_SOCIAL_URLS, OnboardingStepAnswer } from '../types';
import { useOnboardingWizardStepAnalyticsPage } from '../hooks/useOnboardingWizardStepAnalyticsPage';
import { Formik } from 'formik';
import { Form } from 'react-bootstrap';
import { LinkIcon } from '../../assets/icons';
import facebookLogo from '../../assets/images/facebook.png';
import linkedinLogo from '../../assets/images/linkedin.png';
import threadsLogo from '../../assets/images/threads.png';
import instagramLogo from '../../assets/images/instagram.png';
import twitterLogo from '../../assets/images/x.png';

const SOCIAL_URL_FIELDS = [
  {
    label: 'Website',
    name: 'website',
    placeholder: 'https://yourcompany.com',
    icon: <LinkIcon />,
  },
  {
    label: 'Instagram',
    name: 'instagram',
    placeholder: 'your company IG account',
    icon: <img src={instagramLogo} alt="Instagram" style={{ width: 24 }} />,
  },
  {
    label: 'Facebook',
    name: 'facebook',
    placeholder: 'your company FB page',
    icon: <img src={facebookLogo} alt="Facebook" style={{ width: 24 }} />,
  },
  {
    label: 'Threads',
    name: 'threads',
    placeholder: 'your company Threads account',
    icon: <img src={threadsLogo} alt="Threads" style={{ width: 24 }} />,
  },
  {
    label: 'X (Twitter)',
    name: 'twitter',
    placeholder: 'your company X account',
    icon: <img src={twitterLogo} alt="X" style={{ width: 24 }} />,
  },
  {
    label: 'LinkedIn',
    name: 'linkedin',
    placeholder: 'your company Linkedin account',
    icon: <img src={linkedinLogo} alt="LinkedIn" style={{ width: 24 }} />,
  },
] as const;

type SocialUrlFieldName = (typeof SOCIAL_URL_FIELDS)[number]['name'];
type SocialUrlsFormValues = Record<SocialUrlFieldName, string>;

export const SocialUrlsStep = (props: {
  stepIndex: number;
  totalSteps: number;
  onNext: (answers: OnboardingStepAnswer[]) => void;
  onSkip: () => void;
}) => {
  useOnboardingWizardStepAnalyticsPage(ONBOARDING_STEP_SOCIAL_URLS);
  const { stepIndex, totalSteps, onNext, onSkip } = props;

  return (
    <Formik<SocialUrlsFormValues>
      initialValues={SOCIAL_URL_FIELDS.reduce<SocialUrlsFormValues>(
        (acc, f) => ({ ...acc, [f.name]: '' }),
        {} as SocialUrlsFormValues
      )}
      onSubmit={(values) => {
        const answers: OnboardingStepAnswer[] = SOCIAL_URL_FIELDS.filter(
          (f) => (values[f.name] || '').trim().length > 0
        ).map((f) => ({ name: f.name, value: values[f.name].trim() }));
        onNext(answers);
      }}
    >
      {({ values, handleChange, handleBlur, handleSubmit }) => (
        <Form className="onboarding-step__container" onSubmit={handleSubmit}>
          <div>
            Please provide your company website and social media pages (optional).
            <br />
            This helps us personalize your experience and content.
          </div>

          <div className="overflow-hidden">
            {SOCIAL_URL_FIELDS.map((field, idx) => (
              <Form.Group
                className="onboarding-step__row onboarding-social-url__row"
                controlId={`onboarding-social-url__${field.name}`}
                key={field.name}
              >
                <div className="onboarding-social-url__icon" aria-hidden="true">
                  {field.icon}
                </div>
                <div className="onboarding-social-url__label-text" title={field.label}>
                  {field.label}
                </div>
                <input
                  type="url"
                  name={field.name}
                  id={`onboarding-social-url__${field.name}`}
                  className="growegy-form__input onboarding-social-url__input"
                  placeholder={field.placeholder}
                  value={values[field.name] || ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  aria-label={field.label}
                  tabIndex={0}
                  autoFocus={idx === 0}
                />
              </Form.Group>
            ))}
          </div>
          <OnboardingToolbar
            stepIndex={stepIndex}
            totalSteps={totalSteps}
            onNext={handleSubmit}
            onSkip={onSkip}
          />
        </Form>
      )}
    </Formik>
  );
};
